import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { PlusIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/cn'
import { compararPorHora, type PublicacionResumen } from '@/lib/schemas/publicaciones'
import { COLUMNAS_SEMANA, diasDelMes, hoyEnBuenosAires } from '@/features/grilla/formato'
import { CELDA_COMPACTA, piezasQueEntran } from '@/features/grilla/medidas'
import { useEspacioDisponible } from '@/lib/useEspacioDisponible'
import { PiezaCalendario } from './PiezaCalendario'

/** Tope de piezas por día cuando la celda no se mide (tableta). */
const TOPE_POR_CELDA = 3
/** Alto del renglón con los nombres de los días. */
const ALTO_SEMANA = 37
/** Una celda nunca más baja que esto, aunque la pantalla no alcance. */
const CELDA_MINIMA = 64

interface MesCalendarioProps {
  mes: string
  publicaciones: PublicacionResumen[]
  esRastro: boolean
  alAbrir: (id: string) => void
  /** RASTRO: nueva publicación con la fecha del día precargada. */
  alCrearEn: (fecha: string) => void
  /** RASTRO: soltó una pieza en otro día. */
  alMover: (p: PublicacionResumen, fecha: string) => void
}

/**
 * P02 escritorio (§8.3). Siete columnas de lunes a domingo, con las semanas
 * que tiene el mes (cinco casi siempre, a veces cuatro o seis): los días de
 * los meses vecinos se ven atenuados pero con sus piezas.
 *
 * En escritorio el mes entra entero en la pantalla (pedido del 23/09/2026):
 * el alto de la celda sale del lugar que queda, y cada día muestra las piezas
 * que le entran —en formato compacto si la celda es baja, como en un monitor
 * de 1360×768— y un "+N" con las demás.
 *
 * Para RASTRO cada pieza se arrastra a otro día y cada día es un destino. Con
 * el teclado también: dnd-kit expone el arrastre con espacio y flechas, así
 * que reprogramar no depende del mouse (§2.2).
 */
export function MesCalendario({
  mes,
  publicaciones,
  esRastro,
  alAbrir,
  alCrearEn,
  alMover,
}: MesCalendarioProps) {
  const [arrastrando, setArrastrando] = useState<PublicacionResumen | null>(null)
  const sensores = useSensors(
    // Sin distancia mínima, un clic para abrir la pieza se lee como arrastre.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  const [refMes, espacio] = useEspacioDisponible<HTMLDivElement>()
  const todos = diasDelMes(mes)
  // Sólo las semanas que tocan el mes: la sexta, cuando no hace falta, le
  // quitaba alto a las demás.
  const ultimoDelMes = todos.reduce((u, d, i) => (d.slice(0, 7) === mes ? i : u), 0)
  const semanas = Math.ceil((ultimoDelMes + 1) / 7)
  const dias = todos.slice(0, semanas * 7)
  const altoCelda = espacio
    ? Math.max(CELDA_MINIMA, Math.floor((espacio.alto - ALTO_SEMANA) / semanas))
    : null
  const hoy = hoyEnBuenosAires()
  const porDia = new Map<string, PublicacionResumen[]>()
  for (const p of publicaciones) {
    const lista = porDia.get(p.fechaPautada)
    if (lista) lista.push(p)
    else porDia.set(p.fechaPautada, [p])
  }
  // Dentro del día, por hora; las que no tienen hora, al final (DEC-20).
  for (const lista of porDia.values()) lista.sort(compararPorHora)

  const cuadricula = (
    <div
      ref={refMes}
      data-calendario="mes"
      className="grid grid-cols-7 overflow-hidden rounded-tarjeta bg-tarjeta shadow-tarjeta"
      style={
        altoCelda
          ? {
              gridTemplateRows: `${String(ALTO_SEMANA)}px repeat(${String(semanas)}, ${String(altoCelda)}px)`,
            }
          : undefined
      }
    >
      {COLUMNAS_SEMANA.map((c) => (
        <div
          key={c}
          className="border-b border-borde px-3 py-2 text-center text-[13px] font-medium text-tinta-suave"
        >
          {c}
        </div>
      ))}
      {dias.map((dia) => (
        <Celda
          key={dia}
          dia={dia}
          delMes={dia.slice(0, 7) === mes}
          esHoy={dia === hoy}
          piezas={porDia.get(dia) ?? []}
          altoCelda={altoCelda}
          esRastro={esRastro}
          alAbrir={alAbrir}
          alCrearEn={alCrearEn}
        />
      ))}
    </div>
  )

  if (!esRastro) return cuadricula

  return (
    <DndContext
      sensors={sensores}
      onDragStart={(e: DragStartEvent) => {
        setArrastrando(publicaciones.find((p) => p.id === String(e.active.id)) ?? null)
      }}
      onDragEnd={(e: DragEndEvent) => {
        const pieza = arrastrando
        setArrastrando(null)
        const destino = e.over ? String(e.over.id) : null
        // Soltar en el mismo día no es una reprogramación: no deja actividad.
        if (pieza && destino && destino !== pieza.fechaPautada) alMover(pieza, destino)
      }}
      onDragCancel={() => {
        setArrastrando(null)
      }}
    >
      {cuadricula}
      <DragOverlay>
        {arrastrando && (
          <div className="w-40 rounded-lg bg-tarjeta p-1 shadow-tarjeta">
            <PiezaCalendario
              publicacion={arrastrando}
              alAbrir={() => undefined}
              paraRastro
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

function Celda({
  dia,
  delMes,
  esHoy,
  piezas,
  altoCelda,
  esRastro,
  alAbrir,
  alCrearEn,
}: {
  dia: string
  delMes: boolean
  esHoy: boolean
  piezas: PublicacionResumen[]
  /** Alto medido de la celda; `null` en tableta, donde crece con su contenido. */
  altoCelda: number | null
  esRastro: boolean
  alAbrir: (id: string) => void
  alCrearEn: (fecha: string) => void
}) {
  const [verTodas, setVerTodas] = useState(false)
  const { setNodeRef, isOver } = useDroppable({ id: dia, disabled: !esRastro })
  const tope = altoCelda ? piezasQueEntran(altoCelda, piezas.length) : TOPE_POR_CELDA
  const compacta = altoCelda !== null && altoCelda < CELDA_COMPACTA
  const visibles = piezas.slice(0, tope)
  const ocultas = piezas.length - visibles.length
  const numero = Number(dia.slice(8))

  const pieza = (p: PublicacionResumen) =>
    esRastro ? (
      <PiezaArrastrable
        key={p.id}
        publicacion={p}
        alAbrir={alAbrir}
        compacta={compacta}
      />
    ) : (
      <PiezaCalendario key={p.id} publicacion={p} alAbrir={alAbrir} compacta={compacta} />
    )

  return (
    <div
      ref={setNodeRef}
      data-dia={dia}
      className={cn(
        'group relative border-t border-r border-borde last:border-r-0',
        altoCelda ? 'p-1' : 'min-h-28 p-1.5',
        !delMes && 'bg-lienzo/60',
        isOver && 'bg-hoy/10 ring-2 ring-inset ring-hoy ring-dashed',
      )}
    >
      <div className="flex h-5 items-center justify-between gap-1">
        <span
          className={cn(
            'inline-flex size-5 items-center justify-center rounded-full text-[12px]',
            !delMes && 'text-tinta-suave',
            esHoy && 'bg-hoy font-medium text-white',
          )}
        >
          {numero}
        </span>
        <span className="flex items-center gap-0.5">
          {/* El "+N" va en el renglón del número: no le quita lugar a las piezas. */}
          {ocultas > 0 && (
            <button
              type="button"
              onClick={() => {
                setVerTodas(true)
              }}
              className="rounded-full bg-lienzo px-1.5 text-[11px] leading-4 text-tinta-suave hover:bg-borde"
            >
              +{ocultas}
            </button>
          )}
          {esRastro && (
            <button
              type="button"
              aria-label={`Nueva publicación el ${dia}`}
              onClick={() => {
                alCrearEn(dia)
              }}
              className="rounded-full p-0.5 text-tinta-suave opacity-0 transition-opacity hover:bg-lienzo focus-visible:opacity-100 group-hover:opacity-100"
            >
              <PlusIcon className="size-4" aria-hidden />
            </button>
          )}
        </span>
      </div>

      <div
        className={cn('flex flex-col', compacta ? 'mt-0.5 gap-0.5' : 'mt-1.5 gap-0.5')}
      >
        {/* Con el día desplegado, las piezas están sólo en la lista de encima. */}
        {!(verTodas && ocultas > 0) && visibles.map(pieza)}
      </div>

      {/* Todas las del día, desplegadas encima de la celda. */}
      {verTodas && ocultas > 0 && (
        <div
          data-dia-desplegado
          className="absolute inset-x-0 top-0 z-20 flex min-h-full flex-col gap-0.5 rounded-lg bg-tarjeta p-1 shadow-tarjeta ring-1 ring-borde"
        >
          <div className="flex h-5 items-center justify-between">
            <span className="px-1 text-[12px] font-medium">{numero}</span>
            <button
              type="button"
              aria-label="Cerrar el día"
              onClick={() => {
                setVerTodas(false)
              }}
              className="rounded-full p-0.5 text-tinta-suave hover:bg-lienzo"
            >
              <XIcon className="size-3.5" aria-hidden />
            </button>
          </div>
          {piezas.map((p) =>
            esRastro ? (
              <PiezaArrastrable key={p.id} publicacion={p} alAbrir={alAbrir} />
            ) : (
              <PiezaCalendario key={p.id} publicacion={p} alAbrir={alAbrir} />
            ),
          )}
        </div>
      )}
    </div>
  )
}

/** Pieza arrastrable. Sigue abriéndose con un clic corto. */
function PiezaArrastrable({
  publicacion,
  alAbrir,
  compacta,
}: {
  publicacion: PublicacionResumen
  alAbrir: (id: string) => void
  compacta?: boolean | undefined
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: publicacion.id })
  return (
    <div ref={setNodeRef} className={cn('touch-none', isDragging && 'opacity-40')}>
      <PiezaCalendario
        publicacion={publicacion}
        alAbrir={alAbrir}
        paraRastro
        compacta={compacta}
        // El arrastre va en el botón de la pieza, no en un envoltorio: así no
        // queda un botón dentro de otro (axe, nested-interactive).
        arrastre={{
          ref: setActivatorNodeRef,
          props: { ...attributes, ...listeners, role: undefined },
        }}
      />
    </div>
  )
}
